import React, { useEffect, useRef, useState } from "react";
import {
  HospitalSubTitle,
  HospitalTitle,
  IconWrapper,
  LoadingContainer,
  RightIconWrapper,
  TopSection,
  Wrapper,
} from "./index.styled";
import { ReactComponent as BackIcon } from "../../Assets/Icons/BackIcon.svg";
import { ReactComponent as FactSheetCompare } from "../../Assets/Icons/FactSheetCompare.svg";
import { ReactComponent as FactSheetExport } from "../../Assets/Icons/FactSheetExport.svg";
import { ReactComponent as FactSheetStar } from "../../Assets/Icons/FactSheetStar.svg";
import { ReactComponent as PartnerLocation } from "../../Assets/Icons/PartnerLocation.svg";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useAddBookmark, useRemoveBookmark } from "../../hooks/useBookmarks";
import { addHospitalToCompare, removeHospitalFromCompare } from "./../../Redux/compare/compare.actions";
import { Loader } from "../../App";
import {
  formatLocation,
  toTitleCase,
  viewSizeCalculator,
} from "../../utils";
import OptionsList from "../OptionsList";
import { exportOptions } from "../../Configs/config";
import exportFromJSON from "export-from-json";
import * as XLSX from "xlsx-js-style";
import Info from "../Info";
import {
  addCommasToNumber,
  formatAreaSmart,
  formatNumber,
  formatPercentage,
  splitAndClean,
} from "../FactSheetLeft/dataHandler";
import { useSnack } from "../SnackBar";

const FactSheetTopBanner = ({ data, state, toggleState }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const snack = useSnack();
  const addBookmark = useAddBookmark();
  const removeBookmark = useRemoveBookmark();
  const dropdownRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [preventToggle, setPreventToggle] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const { bookmarkList } = useSelector((state) => state.bookmarks);
  const { compareHospitals } = useSelector((state) => state.compare);
  const bookmarksNamesList = bookmarkList?.map((el) => el.name) || [];

  const isBookmarked = bookmarksNamesList
    .map((name) => name?.toLowerCase())
    .includes(data?.name?.toLowerCase());

  const isAddedToCompare = (id) => {
    return compareHospitals.some((el) => el.provider_number === id);
  };

  const removeFromCompare = (id) => {
    dispatch(removeHospitalFromCompare(id));
    snack("Hospital removed from compare successfully");
  };

  const navigateToComparedHospital = () => {
    if (compareHospitals.length >= 20) {
      snack(
        "Max Selection reached! Up to 20 Hospitals can be compared",
        "#FFC640",
        "#333333"
      );
      return;
    }
    if (isAddedToCompare(data?.provider_number)) {
        removeFromCompare(data?.provider_number);
        return;
    }else{
      snack(
        `View all (${compareHospitals.length + 1}) added hospitals in Compare Screen!`,
        "#1F1F1F",
        "#FFFFFF",
        "View",
        () => {
          if (compareHospitals.length + 1 <= 1) {
            snack(
              "Select at least two hospitals to compare!",
              "#FFC640",
              "#333333"
            );
          } else {
            toggleState("isTableVisible");
          }
        }
      );
      const payload = {
        value: data?.name,
        label: toTitleCase(data?.name),
        location: formatLocation(data?.location),
        provider_number: data?.provider_number,
      }
      dispatch(addHospitalToCompare(payload));
    }
  };

  const backToHome = () => {
    // dispatch(resetSelectedState());
    navigate(-1);
  };

  const addToBookmark = async () => {
    setLoading(true);
    const res = await addBookmark(data?.provider_number);
    
    if (res.success) {
      snack(
        "Bookmark added successfully",
        "#1F1F1F",
        "#FFFFFF"
      );
    } else {
      snack(
        `Failed to Add bookmark: ${res.message}`,
        "#FFC640",
        "#333333"
      );
    }

    setLoading(false);
  };

  const removeFromBookmark = async () => {
    setLoading(true);
    const res = await removeBookmark(data?.provider_number);
    
    if (res.success) {
      snack(
        "Bookmark removed successfully",
        "#1F1F1F",
        "#FFFFFF"
      );
    } else {
      snack(
        `Failed to remove: ${res.message}`,
        "#FFC640",
        "#333333"
      );
    }
    setLoading(false);
  };

  const handleBookmarkToggle = () => {
    isBookmarked ? removeFromBookmark() : addToBookmark();
  };

  const onPreventToggle = (flag) => {
    setPreventToggle(flag);
  };

  const handleOptionClick = (option) => {
    setIsOpen(false);
    onExportLocal(option.type);
  };

  const toggleDropdown = () => {
    if (preventToggle) setPreventToggle(false);
    setIsOpen(!isOpen);
  };

  const formatTeachingStatus = (status) => {
    if (status == null || status === "-" || status === "") return "-";
    return ["1", 1, "Y", "Yes", true].includes(status) ? "Yes" : "No";
  };

  const formatQualityValue = (value) => {
    if (
      value == null ||
      value === "-" ||
      value === "" ||
      value === "None" ||
      value === "None%"
    )
      return "N/A";
    return value;
  };

  const onExportLocal = (type) => {
    const fileName = "Hospital-Factsheet";
    const exportType = exportFromJSON.types[type];

    const formattedData = [data].map((item) => {
      return {
        Name: item?.name ?? "-",
        Location: item?.location ?? "-",
        Contact: item.facilities?.contact ?? "-",
        "Street Address": item?.street_addr ?? "-",
        "Zip Code": item?.zip_code ?? "-",
        CBSA: item?.cbsa ?? "-",
        "CBSA Cancer Incidences": item?.cbsa_cancer_incidences ?? "-",

        // Facilities
        Ownership: item.facilities?.ownership ?? "-",
        "Facility Type": item.facilities?.facility_type ?? "-",
        Geography: item.facilities?.geography ?? "-",
        "Hospital Beds":
          addCommasToNumber(item.facilities?.hospital_beds) ?? "-",
        "Patient Volume": item.facilities?.patient_volume ?? "-",
        "Hospital Area": formatAreaSmart(item.facilities?.hospital_area) ?? "-",
        Discharges:
          item.facilities?.discharges != null
            ? item.facilities.discharges.toFixed(2)
            : "-",
        Admissions: addCommasToNumber(item.facilities?.admissions) ?? "-",
        "Teaching Status": formatTeachingStatus(
          item.facilities?.teaching_status,
        ),
        "NPI Number": item.facilities?.npi_number ?? "-",
        "Firm Type": item.facilities?.firm_type ?? "-",
        "Number of Employees":
          addCommasToNumber(item.facilities?.number_employees) ?? "-",
        "Number of Operating Rooms":
          addCommasToNumber(item.facilities?.num_operating_rooms) ?? "-",
        "340B ID": item.facilities?.program_340b_id ?? "-",
        "340B ID Classification":
          item.facilities?.hospital_340b_id_classification ?? "-",
        "Number of Affiliated Physicians":
          addCommasToNumber(item.facilities?.number_affiliated_physicians) ??
          "-",
        Website: item.facilities?.website ?? "-",

        // Financials
        "Gross Revenue": formatNumber(item.financials?.gross_revenue),
        "Net Revenue": formatNumber(item.financials?.net_revenue),
        "Net Patient Revenue": formatNumber(
          item.financials?.net_patient_revenue ?? "-",
        ),
        "Operating Income": formatNumber(
          item.financials?.operating_income ?? "-",
        ),
        "Total Operating Expenses": formatNumber(
          item.financials?.tot_operating_expenses ?? "-",
        ),
        "Net Income": formatNumber(item.financials?.net_income ?? "-"),
        "Net Income Margin":
          item.financials?.net_income_margin &&
          item.financials?.net_income_margin[0] !== "-"
            ? `${item.financials?.net_income_margin}%`
            : "-",
        "Outpatient Revenue": formatNumber(
          item.financials?.outpatient_revenue ?? "-",
        ),
        "Net Revenue Oncology": formatNumber(
          item.financials?.net_revenue_oncology ?? "-",
        ),
        "Revenue to Cost Ratio": item.financials?.revenue_to_cost_ratio ?? "-",
        "Bad Debt Percentage": formatPercentage(
          item.financials?.bad_debt_percentage,
        ),

        // Affiliations
        "Medical School Affiliation": item.medical_school_affiliation ?? "-",

        // Accreditations
        Accreditations: splitAndClean(item.accreditations),

        // Oncology Focus
        "Radiation Therapy":
          addCommasToNumber(item.oncology_focus?.radiation_therapy) ?? "-",
        "Chemo Therapy":
          addCommasToNumber(item.oncology_focus?.chemo_therapy) ?? "-",
        "Surgical Oncology":
          addCommasToNumber(item.oncology_focus?.procedures) ?? "-",

        // Oncology Research
        // "Tumor Board": item.oncology_research?.tumor_board ?? "-",
        // "Participation in Clinical Trials": item.oncology_research?.participation_in_clinical_trials ?? "-",
        // "Research Affiliations": item.oncology_research?.research_affiliations ?? "-",

        // Quality of Care
        NCCN: item.quality_of_care?.nccn ? "Yes" : "No",
        C4QI: item.quality_of_care?.c4qi ? "Yes" : "No",
        "Oncology Mortality": formatQualityValue(
          item.quality_of_care?.oncology_mortality,
        ),
        "Readmissions Rate": formatQualityValue(
          item.quality_of_care?.readmissions_rate,
        ),
        "Overall Rating": formatQualityValue(
          item.quality_of_care?.overall_rating,
        ),
        HCAHPS: formatQualityValue(item.quality_of_care?.hcahps),

        // Accessibility & Inclusivity
        "Geographic Accessibility":
          item.accessibility_inclusivity?.geographic_accessibility ?? "-",

        // Patient Experience
        // "Communication With Nurses/Doctors": item.patient_experience?.communication_with_nurses_doctors ?? "-",
        // "Appointment Wait Times": item.patient_experience?.appointment_wait_times ?? "-",
        // "Cleanliness and Quietness": item.patient_experience?.cleanliness_and_quietness ?? "-",
        // "Likelihood of Recommendation": item.patient_experience?.likelihood_of_recommendation ?? "-",
        // "Cancer Navigation Program": item.patient_experience?.cancer_navigation_program ?? "-",
      };
    });

    if (type === "json") {
      exportFromJSON({ data: formattedData, fileName, exportType: "json" });
      return;
    }

    const keys = Object.keys(formattedData[0]);
    const transposedData = keys.map((key) => ({
      Attribute: key,
      ...Object.fromEntries(
        formattedData.map((item, index) => [`Hospital`, item[key] ?? "-"]),
      ),
    }));

    if (type === "xlsx") {
      const worksheet = XLSX.utils.json_to_sheet(transposedData);

      const range = XLSX.utils.decode_range(worksheet["!ref"]);
      for (let col = range.s.c; col <= range.e.c; col++) {
        const cellRef = XLSX.utils.encode_cell({ r: 0, c: col });
        if (worksheet[cellRef]) {
          worksheet[cellRef].s = { font: { bold: true } };
        }
      }

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Factsheet");
      XLSX.writeFile(workbook, `${fileName}.xlsx`);
      return;
    }

    exportFromJSON({ data: transposedData, fileName, exportType });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setPreventToggle(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <Wrapper>
      <TopSection>
        <Info
          showArrow={false}
          config={{
            tooltipText: "Go Back",
            position: "top",
            offsetY: 10,
          }}
        >
          <IconWrapper
            onClick={backToHome}
            // onClick={() => {
            //   dispatch(resetSelectedState());
            //   navigate("/");
            // }}
          >
            <BackIcon />
          </IconWrapper>
        </Info>
        <RightIconWrapper>
          <Info
            showArrow={false}
            config={{
              tooltipText: "Download",
              position: "top",
              offsetY: 10,
            }}
          >
            <IconWrapper onClick={toggleDropdown} ref={dropdownRef}>
              <FactSheetExport />
              <OptionsList
                options={exportOptions}
                onOptionClick={handleOptionClick}
                selectedOption={exportOptions}
                customWidth={15.75}
                top={3.4}
                showIcon={false}
                right={true}
                onPreventToggle={onPreventToggle}
                showTitle={"Export"}
                customPadding={0.313}
                customBorderRadius={0.625}
                isOpen={isOpen}
              />
            </IconWrapper>
          </Info>

          <Info
            showArrow={false}
            config={{
              tooltipText: "Compare Hospital",
              position: "top",
              offsetY: 10,
            }}
          >
            <IconWrapper onClick={navigateToComparedHospital}>
              <FactSheetCompare
                  style={{
                    fill: isAddedToCompare(data?.provider_number)
                      ? "#F6B51C"
                      : "none",
                  }}
              />
            </IconWrapper>
          </Info>

          <IconWrapper onClick={handleBookmarkToggle}>
            {loading ? (
              <LoadingContainer>
                <Loader
                  height={viewSizeCalculator(1.5)}
                  width={viewSizeCalculator(1.5)}
                />
              </LoadingContainer>
            ) : (
              <Info
                showArrow={false}
                config={{
                  tooltipText: "Bookmark",
                  position: "top",
                  offsetY: 10,
                }}
              >
                <FactSheetStar
                  style={{
                    fill: isBookmarked ? "#F6B51C" : "none",
                  }}
                />
              </Info>
            )}
          </IconWrapper>
        </RightIconWrapper>
      </TopSection>
      <HospitalTitle
          style={{
            display: "flex",
            alignItems: "center",
            gap: viewSizeCalculator(0.35),
          }}
        >
          <span>{toTitleCase(data?.name)}</span>

          {Number(data?.esa) === 1 && (
            <PartnerLocation
              style={{
                flexShrink: 0,
                width: viewSizeCalculator(1.5),
                height: viewSizeCalculator(1.5),
              }}
            />
          )}
      </HospitalTitle>
      <HospitalSubTitle>{formatLocation(data?.location)}</HospitalSubTitle>
      {/* Commented becuase this is part of Phase 2 */}
      {/* <HospitalSubTitle>
        {data?.health_system_name || "N/A"} | {formatLocation(data?.location)}
      </HospitalSubTitle> */}
    </Wrapper>
  );
};

export default FactSheetTopBanner;
